"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-muted/30 border border-border rounded-[2px] animate-in fade-in zoom-in duration-500">
        <h3 className="text-xl font-serif text-primary mb-2">Request Received</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-[2px] border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
          Book Another Consultation
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="rounded-[2px] border-input bg-background focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="rounded-[2px] border-input bg-background focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" {...field} className="rounded-[2px] border-input bg-background focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="subject" rules={{
        required: "Subject is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">Subject</FormLabel>
              <FormControl>
                <Input placeholder="Consultation Topic" {...field} className="rounded-[2px] border-input bg-background focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="message" rules={{
        required: "Message is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/90">Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us more about your needs..." {...field} className="min-h-[160px] rounded-[2px] border-input bg-background focus-visible:ring-primary/20 resize-y" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-[2px]">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-[2px] h-12 px-8 text-base font-medium transition-all shadow-sm">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </> : "Send message"}
        </Button>
      </div>
    </Form>;
}
export default function PrivateConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="privateconsultation" className="py-24 bg-primary relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-secondary mb-6 tracking-tight">
              Book a private consultation
            </h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto text-lg leading-relaxed">Schedule a 1-on-1 discovery call to discuss your relocation strategy and international mobility needs.</p>
          </div>

          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4">
              <FormProvider formId="book_consultation_form_home" sectionName="private-consultation" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
              },
              phone: {
                required: true,
                pattern: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/
              },
              subject: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Send message" successMessage="Thank you for your inquiry. We have received your request for a private consultation and will contact you shortly to schedule a time." errorMessage="We encountered an issue sending your message. Please try again or contact us directly.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}